import { ActionButton, StatusLight } from '@react-spectrum/s2';
import type { ReactNode } from 'react';

// A tooltip-style chip revealed on hover of its wrapper, which must carry
// `group relative`. `align` picks the edge it hangs from: the trailing skip
// button is right-aligned, a left-aligned row region reads better from the
// left. A disabled S2 button doesn't surface its aria-label on hover, and an
// inert row has no control to hover at all, so this is how pointer users see
// the reason at all.
const HoverChip = ({
	children,
	align,
}: {
	children: ReactNode;
	align: 'left' | 'right';
}) => (
	// `w-max` with a `max-w` cap, and NO `whitespace-nowrap`. The chip is absolutely positioned,
	// and an absolutely positioned box still counts toward the scrollable overflow of every
	// ancestor. With `whitespace-nowrap` a long reason laid itself out on one line about 600px
	// past the row, so the chat window could be scrolled sideways — while the chip is `opacity-0`,
	// so there was nothing on screen to explain it. The cap is on the containing block, less the
	// left offset, so the chip fits the width its parent gives it in both alignments.
	<span
		className={`pointer-events-none absolute top-full z-10 mt-1 w-max rounded-md
			bg-gray-800 px-2 py-1 text-xs break-words text-white opacity-0 shadow-md
			transition-opacity group-hover:opacity-100
			${align === 'right' ? 'right-0 max-w-full' : 'left-3 max-w-[calc(100%-0.75rem)]'}`}
	>
		{children}
	</span>
);

export const SetupCardRow = ({
	icon,
	title,
	description,
	onClick,
	descriptionPlacement,
	isInert = false,
	inertReason,
	isCompleted = false,
	isInProgress = false,
	isError = false,
	isLocked = false,
	isSelected = false,
	isSkipped = false,
	showSkip = false,
	canSkip = true,
	skipDisabledReason,
	onSkip,
	onRestore,
}: {
	icon: ReactNode;
	title: string;
	description: string;
	onClick: () => void;
	descriptionPlacement: 'right-status' | 'right-helper';
	isInert?: boolean;
	inertReason?: string;
	isCompleted?: boolean;
	isInProgress?: boolean;
	isError?: boolean;
	isLocked?: boolean;
	isSelected?: boolean;
	isSkipped?: boolean;
	showSkip?: boolean;
	canSkip?: boolean;
	skipDisabledReason?: string;
	onSkip?: () => void;
	onRestore?: () => void;
}) => {
	const muted = isInert || isCompleted || isSkipped;
	const nonClickable = isInert || isLocked || isSkipped;
	const showInertReason = isInert && !!inertReason;
	const showChevron = !nonClickable;
	const statusVariant = isError
		? 'negative'
		: isCompleted
			? 'positive'
			: isInProgress
				? 'informative'
				: 'neutral';
	const selectedBg = isSelected ? 'bg-gray-100' : '';
	const content = (
		<>
			<div
				className={`flex size-10 shrink-0 items-center justify-center ${
					muted ? 'text-gray-400' : 'text-gray-600'
				}`}
			>
				{icon}
			</div>
			{/* `min-w-0` is what lets the title actually truncate — without it the
			    flex item refuses to shrink below its content and a long title
			    wraps to a wall of text instead of clipping. */}
			<div className="flex min-w-0 flex-1 items-center text-left">
				<span
					data-testid="setup-card-row-title"
					className={`truncate text-base font-semibold ${
						muted ? 'text-gray-500' : 'text-gray-900'
					}`}
				>
					{title}
				</span>
			</div>
			{description &&
				(descriptionPlacement === 'right-status' ? (
					<div className="shrink-0 self-center">
						<StatusLight variant={statusVariant} size="S">
							{description}
						</StatusLight>
					</div>
				) : (
					<span
						className={`shrink-0 self-center text-right text-xs font-medium
							${muted ? 'text-gray-400' : 'text-gray-500'}`}
					>
						{description}
					</span>
				))}
		</>
	);

	// The chevron is the rightmost element of the row, AFTER the Skip/Undo button,
	// matching the approved design: [ … description · Skip · › ]. It opens the
	// editor like the row body; it's aria-hidden + not a tab stop so it doesn't
	// duplicate the pressable region as a second labelled control. When a row has
	// no chevron (skipped/inert/locked), an equal-width spacer keeps the Skip/Undo
	// column aligned across rows so they don't drift to the right edge.
	const chevron = showChevron ? (
		<button
			type="button"
			onClick={onClick}
			aria-hidden
			tabIndex={-1}
			className="flex shrink-0 cursor-pointer items-center self-center p-1
				text-gray-400"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 20 20"
				fill="currentColor"
				className="size-5"
			>
				<path
					fillRule="evenodd"
					d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
					clipRule="evenodd"
				/>
			</svg>
		</button>
	) : (
		<span aria-hidden className="size-5 shrink-0 self-center p-1" />
	);

	// The pressable region fills the row body; the row's hover/selected background
	// lives on the OUTER wrapper (see below) so the whole row — body, gap, and
	// chevron column — highlights as one continuous surface.
	// `min-w-0` on BOTH: a flex item's default `min-width:auto` refuses to shrink
	// below its content, so without it here the long title blew the row past its
	// container and pushed the status chip off the right edge — truncate on the
	// inner span alone is a no-op while any ancestor can still grow. Whatever is
	// the direct child of the row flex container has to carry `min-w-0 flex-1`,
	// so when the reason wrapper below is present it must keep them too.
	const pressableRegion = nonClickable ? (
		<div className="flex min-w-0 flex-1 items-center gap-3 rounded-lg py-3 pl-3">
			{content}
			{showInertReason && <span className="sr-only">{inertReason}</span>}
		</div>
	) : (
		<button
			type="button"
			onClick={onClick}
			className="flex min-w-0 flex-1 cursor-pointer items-center gap-3
				rounded-lg py-3 pl-3"
		>
			{content}
		</button>
	);

	// An inert row has no control to hover, so the chip has to hang off the row
	// region itself. This wrapper takes the region's place as the row's flex
	// child, so the row-axis sizing moves onto it — a wrapper without
	// `min-w-0 flex-1` would default to `flex: 0 1 auto` / `min-width: auto` and
	// demote them to a grandchild, where they no longer govern the row axis and
	// the truncation above stops working.
	const rowBody = showInertReason ? (
		<div className="group relative flex min-w-0 flex-1">
			{pressableRegion}
			<HoverChip align="left">{inertReason}</HoverChip>
		</div>
	) : (
		pressableRegion
	);

	// One-click skip: a bordered secondary S2 ActionButton per row (design.md
	// "secondary = white pill"), so it reads clearly as a button next to the
	// chevron. Skipped rows offer one-click "Undo"; the last non-skipped row shows
	// a disabled "Skip" whose reason rides an aria-label (keyboard/SR reachable)
	// plus a CSS hover chip for pointer users. No Menu — so the react-stately
	// "Cannot change the id of an item" crash can't recur.
	const skipDisabled = !isSkipped && !canSkip;
	const skipButton = showSkip ? (
		isSkipped ? (
			<ActionButton
				size="S"
				onPress={onRestore}
				aria-label={`Undo skip for ${title}`}
			>
				Undo
			</ActionButton>
		) : (
			<ActionButton
				size="S"
				isDisabled={skipDisabled}
				onPress={onSkip}
				aria-label={
					skipDisabled
						? `Skip ${title} — ${skipDisabledReason ?? 'unavailable'}`
						: `Skip ${title}`
				}
			>
				Skip
			</ActionButton>
		)
	) : null;

	// Wrap the disabled Skip in a hover chip so pointer users see the reason; a
	// disabled S2 button doesn't surface its aria-label on hover by itself. The
	// button is content-sized, so this wrapper needs no row-axis sizing.
	const trailing =
		skipDisabled && skipButton ? (
			<div className="group relative flex">
				{skipButton}
				<HoverChip align="right">{skipDisabledReason}</HoverChip>
			</div>
		) : (
			skipButton
		);

	// The whole row is one highlight surface: a clickable row tints on hover, and a
	// selected row stays tinted. The Skip/Undo button keeps its own S2 states on
	// top. Inert/locked rows don't tint on hover. gap-3 gives the description, the
	// Skip/Undo button, and the chevron clear breathing room.
	const rowHover = nonClickable ? '' : 'transition-colors hover:bg-gray-50';
	return (
		<div
			data-testid="setup-card-row"
			className={`flex w-full items-center gap-3 rounded-lg pr-3 ${rowHover}
				${selectedBg}`}
		>
			{rowBody}
			{trailing}
			{chevron}
		</div>
	);
};
